import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import { Text, Dropdown, DropdownOptionTextOverflow, DropdownTheme } from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import {
  optionsWithUnavailableItems,
  optionsWithSections,
  simpleOptions,
  optionsWithTextWrap,
  optionsWithTextEllipsis,
} from './helpers';
import { LikeHeart } from '@wix/wix-ui-icons-common/on-stage';
import { classes } from './Dropdown.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper';
import { EditorShell } from '../../test/visual/VisualAsync';

const optionsWithIconAndSubtitles = [
  {
    id: '1',
    value: 'Share 01',
    subtitle: 'Subtitle Share 01',
    isSelectable: true,
    icon: <LikeHeart />,
  },
  {
    id: '2',
    value: 'Share 02',
    subtitle: 'Subtitle Share 02',
    isSelectable: true,
    icon: <LikeHeart />,
  },
];

const optionsWithIcons = [
  {
    id: '1',
    value: 'Share 01',
    isSelectable: true,
    icon: <LikeHeart />,
  },
  {
    id: '2',
    value: 'Share 02',
    isSelectable: true,
    icon: <LikeHeart />,
  },
  {
    id: '3',
    value: 'Share 03',
    isSelectable: true,
    icon: <LikeHeart />,
  },
];

const optionsWithJSXSubtitles = [
  {
    id: '1',
    value: 'Share 01',
    subtitle: (
      <>
        <span>Subtitle 01 Share 01</span>
        <br />
        <span>Subtitle 02 Share 01</span>
      </>
    ),
    isSelectable: true,
  },
  {
    id: '2',
    value: 'Share 02',
    subtitle: (
      <>
        <span>Subtitle 01 Share 02</span>
        <br />
        <span>Subtitle 02 Share 02</span>
      </>
    ),
    isSelectable: true,
  },
];

const optionsWithSuffix = [
  {
    id: '1',
    value: `a very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    long text`,
    isSelectable: true,
    subtitle: 'subtitle',
    suffix: <Text>Suffix 1</Text>,
  },
  {
    id: '2',
    value: 'Share 02',
    isSelectable: true,
    subtitle: 'subtitle',
    suffix: <Text>Suffix 2</Text>,
  },
  {
    id: '3',
    value: 'Share 03',
    isSelectable: true,
    subtitle: 'subtitle',
    icon: <LikeHeart />,
    suffix: <Text>Suffix 3</Text>,
  },
  {
    id: '4',
    value: `a very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    long text with icon`,
    isSelectable: true,
    subtitle: 'subtitle',
    icon: <LikeHeart />,
    suffix: <Text>Suffix 4</Text>,
  },
  {
    id: '5',
    value: `a very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    long text`,
    isSelectable: true,
    textOverflow: DropdownOptionTextOverflow.wrap,
    subtitle: 'subtitle',
    suffix: <Text>Suffix 5</Text>,
  },
  {
    id: '6',
    value: `a very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    very very very very very very very very very very very very very very very very very very very very
    long text with icon`,
    isSelectable: true,
    textOverflow: DropdownOptionTextOverflow.wrap,
    subtitle: 'subtitle',
    icon: <LikeHeart />,
    suffix: <Text>Suffix 6</Text>,
  },
];

class DropdownVisual extends React.Component<any> {
  static defaultProps = {
    mobile: false,
    rtl: false,
    focused: false,
  };

  componentDidMount(): void {
    if (this.props.focused) {
      const dropdownDataHook = this.props.mobileNativeSelect
        ? 'native-select'
        : 'dropdown-base';
      const element: HTMLButtonElement | HTMLSelectElement =
        document.querySelector(`[data-hook="${dropdownDataHook}"]`);

      element.focus();
    }
  }

  render() {
    const { mobile, rtl, newErrorMessage, limitedWidth } = this.props;

    return (
      <EditorShell>
        <div
          dir={rtl ? 'rtl' : 'ltr'}
          className={limitedWidth ? classes.limitedWidth : undefined}
        >
          <TPAComponentsProvider value={{ mobile, rtl }}>
            <VisualTestContainer>
              <Dropdown options={this.props.options ?? []} {...this.props} />
              {newErrorMessage && (
                <div className={classes.elementBelow}>
                  An element below to show the spacing
                </div>
              )}
            </VisualTestContainer>
          </TPAComponentsProvider>
        </div>
      </EditorShell>
    );
  }
}

function getNativeTests() {
  return [
    {
      it: 'Simple',
      props: {
        placeholder: 'Placeholder text',
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'Error',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'New error',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        newErrorMessage: true,
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'New error with message',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        newErrorMessage: true,
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'New error with message / ErrorAppearance - BackgroundAndBorder',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        newErrorMessage: true,
        errorAppearance: ErrorAppearance.BackgroundAndBorder,
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'Complex',
      props: {
        placeholder: 'Placeholder text',
        label: 'Label Text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        options: optionsWithSections,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'With Icon',
      props: {
        placeholder: 'Placeholder text',
        label: 'Label Text',
        options: optionsWithIcons,
        initialSelectedId: optionsWithIcons[0].id,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'With unavailable',
      props: {
        options: optionsWithUnavailableItems,
        initialSelectedId: optionsWithUnavailableItems[2].id,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
      },
    },
    {
      it: 'RTL',
      props: {
        placeholder: 'Placeholder text',
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: true,
        mobileNativeSelect: true,
        rtl: true,
      },
    },
    {
      it: 'Readonly',
      props: {
        placeholder: 'Placeholder text',
        options: simpleOptions,
        mobile: true,
        initialSelectedId: optionsWithUnavailableItems[2].id,
        mobileNativeSelect: true,
        readOnly: true,
      },
    },
  ];
}

function getTests(isMobile = false) {
  return [
    {
      it: 'Simple',
      props: {
        placeholder: 'Placeholder text',
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'Simple label with asterisk',
      props: {
        label: 'Label Text',
        'aria-required': true,
        showAsterisk: true,
        forceContentElementVisibility: true,
        options: simpleOptions,
        mobile: isMobile,
      },
    },
    {
      it: 'Error',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'New error',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        newErrorMessage: true,
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'New error with message',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        newErrorMessage: true,
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'New error with message / ErrorAppearance - BackgroundAndBorder',
      props: {
        placeholder: 'Placeholder text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        errorAppearance: ErrorAppearance.BackgroundAndBorder,
        newErrorMessage: true,
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'Complex',
      props: {
        placeholder: 'Placeholder text',
        label: 'Label Text',
        error: true,
        errorMessage: 'The coupon code is not valid',
        options: optionsWithSections,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'With Icon',
      props: {
        placeholder: 'Placeholder text',
        label: 'Label Text',
        options: optionsWithIconAndSubtitles,
        initialSelectedId: optionsWithIconAndSubtitles[0].id,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'With JSX Subtitles',
      props: {
        placeholder: 'Placeholder text',
        label: 'Label Text',
        options: optionsWithJSXSubtitles,
        initialSelectedId: optionsWithJSXSubtitles[0].id,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'With Suffix',
      props: {
        placeholder: 'Placeholder text',
        label: 'Label Text',
        options: optionsWithSuffix,
        initialSelectedId: optionsWithSuffix[0].id,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'With unavailable',
      props: {
        options: optionsWithUnavailableItems,
        initialSelectedId: optionsWithUnavailableItems[2].id,
        forceContentElementVisibility: true,
        mobile: isMobile,
      },
    },
    {
      it: 'RTL',
      props: {
        placeholder: 'Placeholder text',
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
        rtl: true,
      },
    },
    {
      it: 'RTL with error',
      props: {
        placeholder: 'Placeholder text',
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
        rtl: true,
        error: true,
        errorMessage: 'The coupon code is not valid',
      },
    },
    {
      it: 'DropdownOption with textOverflow wrap',
      props: {
        options: optionsWithTextWrap,
        forceContentElementVisibility: true,
        mobile: isMobile,
        limitedWidth: true,
      },
    },
    {
      it: 'DropdownOption with textOverflow ellipsis',
      props: {
        options: optionsWithTextEllipsis,
        forceContentElementVisibility: true,
        mobile: isMobile,
        limitedWidth: true,
      },
    },
    {
      it: 'DropdownOption with all item padding overrides',
      props: {
        options: optionsWithSuffix,
        forceContentElementVisibility: true,
        mobile: isMobile,
        className: classes.fullItemPaddingOverrides,
      },
    },
    {
      it: 'Dropdown content with padding overrides',
      props: {
        options: simpleOptions,
        forceContentElementVisibility: true,
        mobile: isMobile,
        className: classes.dropdownContentPaddings,
      },
    },
    {
      it: 'Dropdown with padding overrides',
      props: {
        options: optionsWithIconAndSubtitles,
        initialSelectedId: optionsWithIconAndSubtitles[0].id,
        mobile: isMobile,
        className: classes.dropdownOverrides,
      },
    },
    {
      it: 'Readonly',
      props: {
        placeholder: 'Placeholder text',
        initialSelectedId: optionsWithUnavailableItems[2].id,
        options: simpleOptions,
        mobile: isMobile,
        readOnly: true,
      },
    },
  ];
}

visualize('Dropdown', () => {
  story('desktop', () => {
    getTests().map((testConfig) => {
      snap(testConfig.it, <DropdownVisual {...testConfig.props} />);
    });
  });

  story('mobile', () => {
    getTests(true).map((testConfig) => {
      snap(testConfig.it, <DropdownVisual {...testConfig.props} />);
    });
  });

  story('Native', () => {
    getNativeTests().map((testConfig) => {
      snap(testConfig.it, <DropdownVisual {...testConfig.props} />);
    });
  });

  story('wired', () => {
    snap('should show overrides', () => (
      <DropdownVisual
        placeholder="Placeholder text"
        forceContentElementVisibility={true}
        options={optionsWithIcons}
        initialSelectedId={optionsWithIcons[0].id}
        className={classes.fullOverrides}
      />
    ));
    snap('should show readonly overrides', () => (
      <DropdownVisual
        placeholder="Placeholder text"
        options={optionsWithIcons}
        label="Label Text"
        initialSelectedId={optionsWithIcons[0].id}
        className={classes.readOnly}
        readOnly
      />
    ));
    snap('error should always show red border', () => (
      <DropdownVisual
        error
        errorMessage="Error message"
        options={simpleOptions}
        className={classes.root}
      />
    ));
    snap('new error message with styles override', () => (
      <DropdownVisual
        error
        options={simpleOptions}
        newErrorMessage
        className={classes.root}
      />
    ));
    snap(
      'new error message with styles override / Error Appearance - BackgroundAndBorder',
      () => (
        <DropdownVisual
          error
          options={simpleOptions}
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          className={classes.root}
        />
      ),
    );
    snap('new error message with message and styles override', () => (
      <DropdownVisual
        error
        errorMessage="Error message"
        options={simpleOptions}
        newErrorMessage
        className={classes.root}
      />
    ));
    snap(
      'new error message with message and styles override / Error Appearance - BackgroundAndBorder',
      () => (
        <DropdownVisual
          error
          errorMessage="Error message"
          options={simpleOptions}
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          className={classes.root}
        />
      ),
    );
    snap('native new error message with styles override', () => (
      <DropdownVisual
        error
        options={simpleOptions}
        newErrorMessage
        mobile
        mobileNativeSelect
        className={classes.root}
      />
    ));
    snap(
      'native new error message with styles override / Error Appearance - BackgroundAndBorder',
      () => (
        <DropdownVisual
          error
          options={simpleOptions}
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          mobile
          mobileNativeSelect
          className={classes.root}
        />
      ),
    );
    snap('native new error message with message and styles override', () => (
      <DropdownVisual
        error
        errorMessage="Error message"
        options={simpleOptions}
        newErrorMessage
        mobile
        mobileNativeSelect
        className={classes.root}
      />
    ));
    snap(
      'native new error message with message and styles override / Error Appearance - BackgroundAndBorder',
      () => (
        <DropdownVisual
          error
          errorMessage="Error message"
          options={simpleOptions}
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          mobile
          mobileNativeSelect
          className={classes.root}
        />
      ),
    );
    snap('native with style override', () => (
      <DropdownVisual
        options={simpleOptions}
        mobile
        initialSelectedId={simpleOptions[0].id}
        label="Label Text"
        mobileNativeSelect
        className={classes.fullOverrides}
      />
    ));
    snap('native readOnly with style override', () => (
      <DropdownVisual
        options={simpleOptions}
        mobile
        initialSelectedId={simpleOptions[0].id}
        readOnly
        label="Label Text"
        mobileNativeSelect
        className={classes.readOnly}
      />
    ));
    snap('focused should apply hover styles', () => (
      <DropdownVisual
        focused
        options={simpleOptions}
        className={classes.root}
        initialSelectedId={simpleOptions[0].id}
      />
    ));
    snap('native focused should apply hover styles', () => (
      <DropdownVisual
        focused
        mobile
        mobileNativeSelect
        options={simpleOptions}
        className={classes.root}
        initialSelectedId={simpleOptions[0].id}
      />
    ));
    snap(
      'loading state should show spinner when isLoading true - desktop',
      () => (
        <div className={classes.notAnimated}>
          <DropdownVisual
            isLoading
            options={simpleOptions}
            className={classes.root}
            initialSelectedId={simpleOptions[0].id}
          />
        </div>
      ),
    );
    snap(
      'loading state should show spinner when isLoading true - mobile',
      () => (
        <div className={classes.notAnimated}>
          <DropdownVisual
            isLoading
            options={simpleOptions}
            className={classes.root}
            initialSelectedId={simpleOptions[0].id}
            mobile
            mobileNativeSelect
          />
        </div>
      ),
    );
    snap('--wix-ui-tpa-dropdown-main-text-font', () => (
      <div className={classes.fontOverride}>
        <DropdownVisual options={simpleOptions} placeholder="I'm placeholder"/>
        <br />
        <DropdownVisual
          options={simpleOptions}
          placeholder="I'm readonly placeholder"
          readOnly={true}
        />
      </div>
    ));
  });

  story('wst overrides', () => {
    snap(
      'default',
      () => {
        const wstOverrides = {
          '--wst-shade-3-color-rgb': '40, 10, 255',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-primary-background-color-rgb': '240, 170, 170',
          '--wst-paragraph-2-color-rgb': '20, 180, 80',
          '--wst-paragraph-2-font-size': '22px',
          '--wst-paragraph-2-font-weight': 'bold',
          '--wst-paragraph-2-font-style': 'italic',
          '--wst-paragraph-2-font-variant': 'small-caps',
        } as React.CSSProperties;

        return (
          <div style={wstOverrides}>
            <DropdownVisual
              placeholder="Placeholder text"
            />
            <br/>
            <DropdownVisual
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              theme={DropdownTheme.Line}
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              options={simpleOptions}
              mobile
              mobileNativeSelect
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              theme={DropdownTheme.Line}
              options={simpleOptions}
              mobile
              mobileNativeSelect
              initialSelectedId={optionsWithIcons[0].id}
            />
          </div>
        );
      }
    );

    snap('open dropdown', () => {
      const wstOverrides = {
        '--wst-primary-background-color-rgb': '0,155,255',
        '--wst-paragraph-2-color-rgb': '170,0,20',
        '--wst-shade-3-color-rgb': '255,140,20',
        '--wst-paragraph-2-font-family': 'Libre Baskerville',
      } as React.CSSProperties;

      return (
        <div style={wstOverrides}>
          <DropdownVisual options={optionsWithIconAndSubtitles} initialSelectedId={optionsWithIconAndSubtitles[0].id}  forceContentElementVisibility/>
        </div>
      );
    });

    snap(
      'disabled state',
      () => {
        const wstOverrides = {
          "--wst-system-disabled-color-rgb": "255, 110, 0",
        } as React.CSSProperties;

        return (
          <div style={wstOverrides}>
            <DropdownVisual
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
              disabled
            />
            <br/>
            <DropdownVisual
              theme={DropdownTheme.Line}
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
              disabled
            />
            <br/>
            <DropdownVisual
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
              disabled
              mobile
              mobileNativeSelect
            />
            <br/>
            <DropdownVisual
              theme={DropdownTheme.Line}
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
              disabled
              mobile
              mobileNativeSelect
            />
            <br/>
          </div>
        );
      }
    );

    snap(
      'error state',
      () => {
        const wstOverrides = {
          "--wst-system-error-color-rgb": "10, 170, 170",
        } as React.CSSProperties;

        return (
          <div style={wstOverrides}>
            <br/>
            <DropdownVisual
              error
              errorMessage="Error message"
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              error
              newErrorMessage
              errorMessage="Error message new error"
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              theme={DropdownTheme.Line}
              error
              errorMessage="Error message Line theme"
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              theme={DropdownTheme.Line}
              error
              errorMessage="Error message Line theme new error"
              newErrorMessage
              options={simpleOptions}
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              error
              errorMessage="Error message native"
              options={simpleOptions}
              mobile
              mobileNativeSelect
              initialSelectedId={optionsWithIcons[0].id}
            />
            <br/>
            <DropdownVisual
              theme={DropdownTheme.Line}
              error
              errorMessage="Error message native Line theme"
              options={simpleOptions}
              mobile
              mobileNativeSelect
              initialSelectedId={optionsWithIcons[0].id}
            />
          </div>
        );
      }
    );
  })
});
